import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Modal,
  SidePanel,
  type BadgeVariant,
  type ButtonVariant,
} from '@silverfalls/sterling';
import { DetailRow } from '../components/DetailRow.js';
import { useToast } from '../components/Toast.js';
import { formatRelativeDate } from '../lib/format.js';
import {
  reissuePurchaseOrder,
  updatePurchaseOrder,
  type UpdatePurchaseOrderInput,
} from '../api/p2p.js';
import {
  adminLegalNextPoStates,
  fetchApInvoicesForPo,
  fetchPurchaseOrder,
  fetchReceiptsForPo,
  recordGoodsReceipt,
  submitApInvoice,
  updatePurchaseOrderState,
  uploadReceiptAttachment,
  type APInvoiceIntakeChannel,
  type APInvoiceRow,
  type APInvoiceState,
  type PurchaseOrderRow,
  type PurchaseOrderState,
  type ReceiptAttachment,
  type ReceiptKind,
  type ReceiptRow,
  type RecordGoodsReceiptLineInput,
  type SubmitAPInvoiceLineInput,
} from '../api/p2p.js';

const REQUESTING_USER = 'u-ui-skeleton';

type DialogState =
  | { open: false }
  | { open: true; kind: 'RECEIPT' }
  | { open: true; kind: 'INVOICE' };

export function PurchaseOrderDetail(): JSX.Element {
  const { ref } = useParams<{ ref: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [po, setPo] = useState<PurchaseOrderRow | null>(null);
  const [receipts, setReceipts] = useState<ReadonlyArray<ReceiptRow>>([]);
  const [invoices, setInvoices] = useState<ReadonlyArray<APInvoiceRow>>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>({ open: false });
  const [transitioning, setTransitioning] = useState<PurchaseOrderState | null>(null);

  const load = useCallback(async () => {
    if (!ref) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [poRow, receiptRows, invoiceRows] = await Promise.all([
        fetchPurchaseOrder(ref),
        fetchReceiptsForPo(ref),
        fetchApInvoicesForPo(ref),
      ]);
      setPo(poRow);
      setReceipts(receiptRows);
      setInvoices(invoiceRows);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [ref]);

  useEffect(() => {
    void load();
  }, [load]);

  const [pendingDestructive, setPendingDestructive] = useState<PurchaseOrderState | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [reissuing, setReissuing] = useState(false);

  const doTransition = useCallback(
    async (toState: PurchaseOrderState) => {
      if (!po) return;
      setTransitioning(toState);
      try {
        const result = await updatePurchaseOrderState({
          poRef: po.poRef,
          toState,
          requestingUser: REQUESTING_USER,
        });
        if (result.__typename === 'UpdatePurchaseOrderStateFailure') {
          toast.push(`${result.class}: ${JSON.stringify(result.details)}`, 'error');
        } else {
          toast.push(`${result.fromState} → ${result.toState}`, 'success');
          await load();
        }
      } catch (err) {
        toast.push(err instanceof Error ? err.message : String(err), 'error');
      } finally {
        setTransitioning(null);
      }
    },
    [po, load, toast],
  );

  const onReissue = useCallback(async () => {
    if (!po) return;
    if (
      !confirm(
        `Reissue ${po.poRef} as a new DRAFT PO? Lines copy verbatim; the cancelled PO stays cancelled.`,
      )
    ) {
      return;
    }
    setReissuing(true);
    try {
      const result = await reissuePurchaseOrder({
        poRef: po.poRef,
        requestingUser: REQUESTING_USER,
      });
      if (result.__typename === 'ReissuePurchaseOrderFailure') {
        toast.push(`${result.class}: ${JSON.stringify(result.details)}`, 'error');
      } else {
        toast.push(`Re-issued as ${result.newPoRef}`, 'success');
        navigate(`/purchase-orders/${result.newPoRef}`);
      }
    } catch (err) {
      toast.push(err instanceof Error ? err.message : String(err), 'error');
    } finally {
      setReissuing(false);
    }
  }, [po, navigate, toast]);

  const onTransition = useCallback(
    async (toState: PurchaseOrderState) => {
      if (!po) return;
      if (toState === 'CANCELLED' || toState === 'CLOSED') {
        setPendingDestructive(toState);
        return;
      }
      await doTransition(toState);
    },
    [po, doTransition],
  );

  if (loading) return <div className="t-body-sm">Loading purchase order…</div>;
  if (loadError) {
    return (
      <div className="alert alert-neg" role="alert">
        Couldn't load PO: {loadError}
      </div>
    );
  }
  if (!po) {
    return (
      <div className="empty-state">
        <p>Purchase order not found: {ref}</p>
        <p>
          <Link to="/purchase-orders">Back to purchase orders</Link>
        </p>
      </div>
    );
  }

  const receivable = isReceivable(po.state);
  const linesWithRemaining = po.lines.map((l) => ({
    ...l,
    remaining: subtract(l.quantity, l.receivedToDate),
  }));
  const hasRemaining = linesWithRemaining.some((l) => Number(l.remaining) > 0);

  return (
    <section className="vendor-detail">
      <div className="screen-header">
        <div className="entity-header">
          <Link to="/purchase-orders" className="back-link">
            ← Purchase orders
          </Link>
          <h2>{po.vendor?.legalName ?? '—'}</h2>
          <div className="entity-ref mono">{po.poRef}</div>
          <div className="entity-summary">
            <span className="entity-summary-part">
              <Badge variant={poStateBadgeVariant(po.state)} dot={false}>{po.state}</Badge>
            </span>
            <span className="entity-summary-part">{po.opcoRef}</span>
            <span className="entity-summary-part mono">
              {po.grandTotal} {po.currencyCode}
            </span>
            <span className="entity-summary-part mono">{formatRelativeDate(po.createdAt)}</span>
          </div>
        </div>
        <div className="header-right-stack">
          <div className="state-actions">
            {receivable && hasRemaining && (
              <Button
                variant="primary"
                size="sm"
                icon="inbox"
                onClick={() => setDialog({ open: true, kind: 'RECEIPT' })}
              >
                Record receipt
              </Button>
            )}
            {po.lines.some((l) => Number(l.receivedToDate) > 0) && (
              <Button
                variant="secondary"
                size="sm"
                icon="file-text"
                onClick={() => setDialog({ open: true, kind: 'INVOICE' })}
              >
                Submit invoice
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              icon="pencil"
              onClick={() => setEditOpen(true)}
              disabled={transitioning !== null}
            >
              Edit
            </Button>
            {po.state === 'CANCELLED' && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void onReissue()}
                loading={reissuing}
                title="Mint a new DRAFT PO copying header + lines from this cancelled one"
              >
                Re-issue
              </Button>
            )}
            {adminLegalNextPoStates(po.state).map((next) => (
              <Button
                key={next}
                variant={poActionVariant(next)}
                size="sm"
                onClick={() => onTransition(next)}
                disabled={transitioning !== null}
                loading={transitioning === next}
              >
                {poActionLabel(next)}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {po.reissueOfPoRef && (
        <div className="alert alert-warn" role="status">
          <strong>This is a re-issue of {' '}
            <Link to={`/purchase-orders/${po.reissueOfPoRef}`} className="mono">
              {po.reissueOfPoRef.slice(0, 24)}…
            </Link>
          </strong> — lines were copied from the cancelled original.
        </div>
      )}

      <div className="detail-grid">
        <div className="detail-card">
          <h3 className="detail-card-title">Header</h3>
          <div className="detail-rows">
            <DetailRow label="Subtotal" value={po.subtotal} mono />
            <DetailRow label="Notes" value={po.notes ?? '—'} />
            <DetailRow label="Created by" value={po.createdBy} mono />
            <DetailRow label="Created at" value={formatDateTime(po.createdAt)} mono />
            {po.reissueOfPoRef && (
              <DetailRow
                label="Re-issue of"
                value={
                  <Link to={`/purchase-orders/${po.reissueOfPoRef}`} className="mono">
                    {po.reissueOfPoRef}
                  </Link>
                }
              />
            )}
          </div>
        </div>
        <div className="detail-card">
          <h3 className="detail-card-title">Receipts ({receipts.length})</h3>
          {receipts.length === 0 ? (
            <p className="cell-secondary">No receipts recorded yet.</p>
          ) : (
            <div className="detail-rows">
              {receipts.map((r) => {
                const totalQty = r.lines.reduce((sum, l) => sum + Number(l.receivedQuantity), 0);
                const attachmentSummary =
                  r.attachments.length > 0
                    ? ` · ${r.attachments.length} attachment${r.attachments.length === 1 ? '' : 's'}`
                    : '';
                return (
                  <div key={r.receiptRef} className="detail-row">
                    <span className="detail-row-label mono">
                      <Badge variant={r.kind === 'SERVICE' ? 'info' : 'success'} dot={false}>
                        {r.kind}
                      </Badge>{' '}
                      <Link to={`/receipts/${r.receiptRef}`}>
                        {r.receiptRef.slice(0, 16)}…
                      </Link>
                    </span>
                    <span className="detail-row-value">
                      {r.lines.length} {r.lines.length === 1 ? 'line' : 'lines'} ·{' '}
                      {totalQty.toFixed(4)} units · {formatDateTime(r.receivedAt)}
                      {attachmentSummary}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="detail-card">
          <h3 className="detail-card-title">Invoices ({invoices.length})</h3>
          {invoices.length === 0 ? (
            <p className="cell-secondary">No invoices submitted yet.</p>
          ) : (
            <div className="detail-rows">
              {invoices.map((inv) => (
                <div key={inv.invoiceRef} className="detail-row">
                  <span className="detail-row-label">
                    <Link to={`/ap-invoices/${inv.invoiceRef}`}>{inv.invoiceNumber}</Link>
                  </span>
                  <span className="detail-row-value">
                    <Badge variant={invoiceStateBadgeVariant(inv.state)} dot={false}>
                      {inv.state}
                    </Badge>{' '}
                    · {inv.total} {inv.currencyCode} · {formatDateTime(inv.createdAt)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="po-lines">
        <div className="po-lines-header">
          <span className="form-label">Lines</span>
        </div>
        <table className="po-lines-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Description</th>
              <th>UoM</th>
              <th>Ordered</th>
              <th>Received</th>
              <th>Remaining</th>
              <th>Unit price</th>
              <th>Line total</th>
              <th>GL</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {linesWithRemaining.map((l) => (
              <tr key={l.lineRef}>
                <td className="mono">{l.lineNumber}</td>
                <td>{l.description}</td>
                <td>{l.unitOfMeasure}</td>
                <td className="mono">{l.quantity}</td>
                <td className="mono">{l.receivedToDate}</td>
                <td className="mono">{l.remaining}</td>
                <td className="mono">{l.unitPrice}</td>
                <td className="mono">{l.lineTotal}</td>
                <td className="mono">{l.defaultGLAccount}</td>
                <td>{l.pricingSource}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dialog.open && dialog.kind === 'RECEIPT' && (
        <RecordReceiptDialog
          po={po}
          onClose={() => setDialog({ open: false })}
          onSuccess={(newState) => {
            setDialog({ open: false });
            toast.push(`Receipt recorded · ${newState}`, 'success');
            void load();
          }}
          onError={(message) => toast.push(message, 'error')}
        />
      )}
      {dialog.open && dialog.kind === 'INVOICE' && (
        <SubmitInvoiceDialog
          po={po}
          onClose={() => setDialog({ open: false })}
          onSuccess={(newState) => {
            setDialog({ open: false });
            toast.push(`Invoice submitted · ${newState}`, 'success');
            void load();
          }}
          onError={(message) => toast.push(message, 'error')}
        />
      )}

      <Modal
        open={pendingDestructive !== null}
        title={
          pendingDestructive === 'CANCELLED'
            ? `Cancel ${po.poRef.slice(0, 16)}…`
            : `Close ${po.poRef.slice(0, 16)}…`
        }
        variant={pendingDestructive === 'CANCELLED' ? 'danger' : 'default'}
        confirmLabel={pendingDestructive === 'CANCELLED' ? 'Cancel PO' : 'Close PO'}
        {...(pendingDestructive === 'CANCELLED' ? { typeToConfirm: po.poRef } : {})}
        onCancel={() => setPendingDestructive(null)}
        onConfirm={() => {
          const next = pendingDestructive;
          setPendingDestructive(null);
          if (next) void doTransition(next);
        }}
      >
        {pendingDestructive === 'CANCELLED'
          ? 'Cancelling the PO is terminal. Open receipts against it stay in history but no new activity is allowed. Type the PO ref to confirm.'
          : 'Closing the PO marks it complete. Remaining unreceived qty is forfeited; the PO can\'t be re-opened.'}
      </Modal>

      {editOpen && (
        <EditPurchaseOrderDialog
          po={po}
          onClose={() => setEditOpen(false)}
          onSaved={(changedFields) => {
            setEditOpen(false);
            if (changedFields.length === 0) toast.push('No changes', 'info');
            else toast.push(`Saved ${changedFields.length} change(s)`, 'success');
            void load();
          }}
          onError={(message) => toast.push(message, 'error')}
        />
      )}
    </section>
  );
}

interface EditPurchaseOrderDialogProps {
  readonly po: PurchaseOrderRow;
  readonly onClose: () => void;
  readonly onSaved: (changedFields: ReadonlyArray<string>) => void;
  readonly onError: (message: string) => void;
}

function EditPurchaseOrderDialog({
  po,
  onClose,
  onSaved,
  onError,
}: EditPurchaseOrderDialogProps): JSX.Element {
  const [notes, setNotes] = useState(po.notes ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setSubmitting(true);
      setError(null);

      const trimmed = notes.trim();
      const cur = po.notes ?? '';
      type MutableInput = {
        -readonly [K in keyof UpdatePurchaseOrderInput]: UpdatePurchaseOrderInput[K];
      };
      const working: MutableInput = { poRef: po.poRef, requestingUser: REQUESTING_USER };
      if (trimmed !== cur) {
        if (trimmed === '') working.clear = ['notes'];
        else working.notes = trimmed;
      }

      try {
        const result = await updatePurchaseOrder(working);
        if (result.__typename === 'UpdatePurchaseOrderFailure') {
          const msg = `${result.class}: ${JSON.stringify(result.details)}`;
          setError(msg);
          onError(msg);
          setSubmitting(false);
          return;
        }
        onSaved(result.changedFields);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        onError(msg);
        setSubmitting(false);
      }
    },
    [po, notes, onSaved, onError],
  );

  return (
    <SidePanel
      open
      title="Edit purchase order"
      width={640}
      onClose={onClose}
      onSave={() => formRef.current?.requestSubmit()}
      dirty={!submitting}
      saveLabel={submitting ? 'Saving…' : 'Save changes'}
    >
      <form ref={formRef} className="form-grid" onSubmit={onSubmit}>
        <label className="form-row form-row-full">
          <span className="form-label">Notes</span>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Internal notes — empty to clear"
          />
        </label>
        {error && <div className="alert alert-neg" role="alert">{error}</div>}
      </form>
    </SidePanel>
  );
}

function poActionLabel(toState: PurchaseOrderState): string {
  switch (toState) {
    case 'APPROVED':
      return 'Approve';
    case 'TRANSMITTED':
      return 'Transmit';
    case 'ACKNOWLEDGED':
      return 'Acknowledge';
    case 'OPEN':
      return 'Open';
    case 'CANCELLED':
      return 'Cancel';
    case 'CLOSED':
      return 'Close';
    case 'PARTIALLY_RECEIVED':
    case 'FULLY_RECEIVED':
      // Receipt-driven edges aren't actually offered as buttons; fall back
      // to the raw value for completeness.
      return toState;
    case 'DRAFT':
      return 'Move to draft';
  }
}

function poActionVariant(toState: PurchaseOrderState): ButtonVariant {
  switch (toState) {
    case 'APPROVED':
    case 'TRANSMITTED':
    case 'ACKNOWLEDGED':
    case 'OPEN':
    case 'CLOSED':
      return 'primary';
    case 'CANCELLED':
      return 'destructive';
    default:
      return 'secondary';
  }
}

function invoiceStateBadgeVariant(state: APInvoiceState): BadgeVariant {
  switch (state) {
    case 'MATCHED':
    case 'APPROVED':
      return 'success';
    case 'RECEIVED':
      return 'info';
    case 'VARIANCE_REVIEW':
    case 'ON_HOLD':
      return 'warning';
    case 'REJECTED':
    case 'CANCELLED':
      return 'neutral';
  }
}

function isReceivable(state: PurchaseOrderState): boolean {
  return state === 'OPEN' || state === 'ACKNOWLEDGED' || state === 'PARTIALLY_RECEIVED';
}

function poStateBadgeVariant(state: PurchaseOrderState): BadgeVariant {
  switch (state) {
    case 'OPEN':
    case 'FULLY_RECEIVED':
      return 'success';
    case 'DRAFT':
    case 'APPROVED':
    case 'TRANSMITTED':
    case 'ACKNOWLEDGED':
    case 'PARTIALLY_RECEIVED':
      return 'info';
    case 'CLOSED':
      return 'neutral';
    case 'CANCELLED':
      return 'warning';
  }
}

function subtract(ordered: string, received: string): string {
  const r = Number(ordered) - Number(received);
  return r.toFixed(4);
}

function formatDateTime(iso: string): string {
  return new Date(iso).toISOString().slice(0, 19).replace('T', ' ');
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}


// ---------------------------------------------------------------------------
// Record receipt dialog
// ---------------------------------------------------------------------------

interface RecordReceiptDialogProps {
  readonly po: PurchaseOrderRow;
  readonly onClose: () => void;
  readonly onSuccess: (newState: PurchaseOrderState) => void;
  readonly onError: (message: string) => void;
}

interface DraftAttachment {
  readonly id: string;
  ref: string;
  filename: string;
  mimeType?: string;
  sizeBytes?: number;
  uploading?: boolean;
  error?: string;
}

function RecordReceiptDialog({
  po,
  onClose,
  onSuccess,
  onError,
}: RecordReceiptDialogProps): JSX.Element {
  const [quantities, setQuantities] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const line of po.lines) {
      init[line.lineRef] = '';
    }
    return init;
  });
  const [notes, setNotes] = useState('');
  const [kind, setKind] = useState<ReceiptKind>('GOODS');
  const [attachments, setAttachments] = useState<ReadonlyArray<DraftAttachment>>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const addAttachment = () =>
    setAttachments((prev) => [
      ...prev,
      { id: crypto.randomUUID(), ref: '', filename: '' },
    ]);
  const removeAttachment = (id: string) =>
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  const updateAttachment = (id: string, patch: Partial<DraftAttachment>) =>
    setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));

  const uploadAndAttach = async (file: File) => {
    const id = crypto.randomUUID();
    // Optimistic row so the user sees progress immediately.
    setAttachments((prev) => [
      ...prev,
      {
        id,
        ref: '',
        filename: file.name,
        uploading: true,
      },
    ]);
    try {
      const result = await uploadReceiptAttachment(file);
      setAttachments((prev) =>
        prev.map((a) =>
          a.id === id
            ? {
                ...a,
                ref: result.ref,
                filename: result.filename,
                ...(result.mimeType != null ? { mimeType: result.mimeType } : {}),
                ...(result.sizeBytes != null ? { sizeBytes: result.sizeBytes } : {}),
                uploading: false,
              }
            : a,
        ),
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setAttachments((prev) =>
        prev.map((a) =>
          a.id === id ? { ...a, uploading: false, error: msg } : a,
        ),
      );
    }
  };

  const onFilesPicked = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    // Sequential to keep error reporting predictable; uploads are small.
    for (const file of Array.from(files)) {
      await uploadAndAttach(file);
    }
  };

  // Only show lines that still have remaining quantity to receive.
  const receivableLines = po.lines
    .map((l) => ({ line: l, remaining: subtract(l.quantity, l.receivedToDate) }))
    .filter((row) => Number(row.remaining) > 0);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    const lines: RecordGoodsReceiptLineInput[] = [];
    for (const { line, remaining } of receivableLines) {
      const raw = quantities[line.lineRef]?.trim();
      if (!raw || raw === '0') continue;
      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0) {
        setError(`Line ${line.lineNumber}: receive qty must be a positive number`);
        setSubmitting(false);
        return;
      }
      if (n > Number(remaining)) {
        setError(`Line ${line.lineNumber}: receive qty ${raw} exceeds remaining ${remaining}`);
        setSubmitting(false);
        return;
      }
      lines.push({ poLineRef: line.lineRef, receivedQuantity: raw });
    }

    if (lines.length === 0) {
      setError('Enter a positive received quantity on at least one line');
      setSubmitting(false);
      return;
    }

    let apiAttachments: ReceiptAttachment[] | undefined;
    if (kind === 'SERVICE') {
      // Validate every attachment row is filled in before submission so the
      // user gets a clearer error than the server's SERVICE_REQUIRES_ATTACHMENT.
      if (attachments.length === 0) {
        setError('SERVICE receipts require at least one attachment (timesheet, deliverable, …)');
        setSubmitting(false);
        return;
      }
      const pendingUploads = attachments.filter((a) => a.uploading);
      if (pendingUploads.length > 0) {
        setError(`Wait for ${pendingUploads.length} upload(s) to finish`);
        setSubmitting(false);
        return;
      }
      const failedUploads = attachments.filter((a) => a.error);
      if (failedUploads.length > 0) {
        setError(
          `Resolve upload errors first: ${failedUploads.map((a) => a.filename).join(', ')}`,
        );
        setSubmitting(false);
        return;
      }
      const rowErrors: string[] = [];
      const built: ReceiptAttachment[] = [];
      const nowIso = new Date().toISOString();
      for (const [idx, a] of attachments.entries()) {
        const ref = a.ref.trim();
        const filename = a.filename.trim();
        if (ref === '' || filename === '') {
          rowErrors.push(`Attachment ${idx + 1}: ref and filename are both required`);
          continue;
        }
        built.push({
          ref,
          filename,
          ...(a.mimeType ? { mimeType: a.mimeType } : {}),
          ...(a.sizeBytes != null ? { sizeBytes: a.sizeBytes } : {}),
          uploadedAt: nowIso,
        });
      }
      if (rowErrors.length > 0) {
        setError(rowErrors.join('; '));
        setSubmitting(false);
        return;
      }
      apiAttachments = built;
    }

    try {
      const result = await recordGoodsReceipt({
        poRef: po.poRef,
        lines,
        notes: notes.trim() === '' ? undefined : notes.trim(),
        receivingUser: REQUESTING_USER,
        kind,
        ...(apiAttachments ? { attachments: apiAttachments } : {}),
      });
      if (result.__typename === 'RecordGoodsReceiptFailure') {
        const msg = `${result.class}: ${JSON.stringify(result.details)}`;
        setError(msg);
        onError(msg);
        setSubmitting(false);
        return;
      }
      onSuccess(result.newPoState);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      onError(msg);
      setSubmitting(false);
    }
  };

  return (
    <SidePanel
      open
      title={`Record receipt — ${po.vendor?.legalName ?? po.poRef}`}
      width={640}
      onClose={onClose}
      onSave={() => formRef.current?.requestSubmit()}
      dirty={!submitting}
      saveLabel={submitting ? 'Recording…' : 'Record receipt'}
    >
      <form ref={formRef} onSubmit={onSubmit}>
          <div className="form-row" role="radiogroup" aria-label="Receipt kind">
            <span className="form-label">Receipt kind</span>
            <div>
              <label style={{ marginRight: 16 }}>
                <input
                  type="radio"
                  name="receipt-kind"
                  value="GOODS"
                  checked={kind === 'GOODS'}
                  onChange={() => setKind('GOODS')}
                />{' '}
                Goods (physical delivery)
              </label>
              <label>
                <input
                  type="radio"
                  name="receipt-kind"
                  value="SERVICE"
                  checked={kind === 'SERVICE'}
                  onChange={() => setKind('SERVICE')}
                />{' '}
                Service (rendered; requires attachment)
              </label>
            </div>
          </div>

          {receivableLines.length === 0 ? (
            <div className="alert alert-neg" role="alert">
              All lines on this PO are already fully received.
            </div>
          ) : (
            <table className="po-lines-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Description</th>
                  <th>UoM</th>
                  <th>Ordered</th>
                  <th>Received</th>
                  <th>Remaining</th>
                  <th>Receive now</th>
                </tr>
              </thead>
              <tbody>
                {receivableLines.map(({ line, remaining }) => (
                  <tr key={line.lineRef}>
                    <td className="mono">{line.lineNumber}</td>
                    <td>{line.description}</td>
                    <td>{line.unitOfMeasure}</td>
                    <td className="mono">{line.quantity}</td>
                    <td className="mono">{line.receivedToDate}</td>
                    <td className="mono">{remaining}</td>
                    <td>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={quantities[line.lineRef] ?? ''}
                        onChange={(e) =>
                          setQuantities((prev) => ({ ...prev, [line.lineRef]: e.target.value }))
                        }
                        className="po-line-num"
                        placeholder="0"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <label className="form-row" style={{ marginTop: 16 }}>
            <span className="form-label">Notes (optional)</span>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Packing slip ref, condition notes, …"
            />
          </label>

          {kind === 'SERVICE' && (
            <div className="po-lines" style={{ marginTop: 16 }}>
              <div className="po-lines-header">
                <span className="form-label">
                  Attachments (≥1 required for SERVICE)
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <label
                    className="btn btn-sm btn-primary"
                    style={{ cursor: 'pointer' }}
                  >
                    Upload file…
                    <input
                      type="file"
                      multiple
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        void onFilesPicked(e.target.files);
                        // Allow re-selecting the same file after a failed upload.
                        e.target.value = '';
                      }}
                      accept="application/pdf,image/png,image/jpeg,image/heic,image/webp,text/csv,text/plain,.xlsx,.xls,.doc,.docx"
                    />
                  </label>
                  <button type="button" className="btn btn-sm" onClick={addAttachment}>
                    + Manual ref
                  </button>
                </div>
              </div>
              {attachments.length === 0 ? (
                <p className="cell-secondary">
                  Add a timesheet, deliverable, or certification doc reference.
                </p>
              ) : (
                <table className="po-lines-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Reference (URL / R2 path)</th>
                      <th>Filename</th>
                      <th aria-label="actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {attachments.map((a, idx) => (
                      <tr key={a.id}>
                        <td className="mono">
                          {idx + 1}
                          {a.uploading && (
                            <span
                              className="badge badge-info"
                              style={{ marginLeft: 6 }}
                            >
                              uploading…
                            </span>
                          )}
                          {a.error && (
                            <span
                              className="badge badge-neg"
                              style={{ marginLeft: 6 }}
                              title={a.error}
                            >
                              FAILED
                            </span>
                          )}
                          {!a.uploading && !a.error && a.ref !== '' && a.sizeBytes != null && (
                            <span
                              className="cell-secondary"
                              style={{ marginLeft: 6 }}
                            >
                              {formatBytes(a.sizeBytes)}
                            </span>
                          )}
                        </td>
                        <td>
                          <input
                            type="text"
                            value={a.ref}
                            onChange={(e) => updateAttachment(a.id, { ref: e.target.value })}
                            placeholder="r2://anvil-uploads/receipts/2026-05/timesheet-001.pdf"
                            disabled={a.uploading}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={a.filename}
                            onChange={(e) =>
                              updateAttachment(a.id, { filename: e.target.value })
                            }
                            placeholder="timesheet-001.pdf"
                            disabled={a.uploading}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => removeAttachment(a.id)}
                            aria-label="Remove attachment"
                            disabled={a.uploading}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {error && (
            <div className="alert alert-neg" role="alert">
              {error}
            </div>
          )}

      </form>
    </SidePanel>
  );
}

// ---------------------------------------------------------------------------
// Submit invoice dialog
// ---------------------------------------------------------------------------

const INTAKE_CHANNELS: ReadonlyArray<APInvoiceIntakeChannel> = [
  'MANUAL_ENTRY',
  'EMAIL',
  'VENDOR_PORTAL',
  'EDI',
  'AI_EXTRACTION',
];

interface SubmitInvoiceDialogProps {
  readonly po: PurchaseOrderRow;
  readonly onClose: () => void;
  readonly onSuccess: (newState: APInvoiceState) => void;
  readonly onError: (message: string) => void;
}

interface DraftInvoiceLine {
  readonly poLineRef: string;
  readonly description: string;
  readonly defaultQty: string; // receivedToDate
  readonly defaultPrice: string; // PO unit price
  quantity: string;
  unitPrice: string;
  enabled: boolean;
}

function SubmitInvoiceDialog({
  po,
  onClose,
  onSuccess,
  onError,
}: SubmitInvoiceDialogProps): JSX.Element {
  // Only lines with any receipts qualify for invoicing.
  const eligibleLines = po.lines.filter((l) => Number(l.receivedToDate) > 0);

  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [intakeChannel, setIntakeChannel] = useState<APInvoiceIntakeChannel>('MANUAL_ENTRY');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<ReadonlyArray<DraftInvoiceLine>>(() =>
    eligibleLines.map((l) => ({
      poLineRef: l.lineRef,
      description: l.description,
      defaultQty: l.receivedToDate,
      defaultPrice: l.unitPrice,
      quantity: l.receivedToDate,
      unitPrice: l.unitPrice,
      enabled: true,
    })),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const updateLine = (poLineRef: string, patch: Partial<DraftInvoiceLine>) => {
    setLines((prev) =>
      prev.map((l) => (l.poLineRef === poLineRef ? { ...l, ...patch } : l)),
    );
  };

  const computedTotal = lines
    .filter((l) => l.enabled)
    .reduce((sum, l) => {
      const q = Number(l.quantity);
      const p = Number(l.unitPrice);
      if (!Number.isFinite(q) || !Number.isFinite(p)) return sum;
      return sum + q * p;
    }, 0)
    .toFixed(4);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    if (invoiceNumber.trim() === '') {
      setError('Invoice number is required');
      setSubmitting(false);
      return;
    }

    const apiLines: SubmitAPInvoiceLineInput[] = [];
    for (const l of lines) {
      if (!l.enabled) continue;
      const q = Number(l.quantity);
      const p = Number(l.unitPrice);
      if (!Number.isFinite(q) || q <= 0) {
        setError(`Line for ${l.poLineRef.slice(0, 10)}…: qty must be > 0`);
        setSubmitting(false);
        return;
      }
      if (!Number.isFinite(p) || p < 0) {
        setError(`Line for ${l.poLineRef.slice(0, 10)}…: price must be >= 0`);
        setSubmitting(false);
        return;
      }
      apiLines.push({
        poLineRef: l.poLineRef,
        description: l.description.trim() === '' ? '(billed item)' : l.description,
        quantity: l.quantity.trim(),
        unitPrice: l.unitPrice.trim(),
      });
    }

    if (apiLines.length === 0) {
      setError('Enable at least one line');
      setSubmitting(false);
      return;
    }

    try {
      const result = await submitApInvoice({
        vendorRef: po.vendorRef,
        opcoRef: po.opcoRef,
        invoiceNumber: invoiceNumber.trim(),
        invoiceDate,
        currencyCode: po.currencyCode,
        lines: apiLines,
        intakeChannel,
        notes: notes.trim() === '' ? undefined : notes.trim(),
        requestingUser: REQUESTING_USER,
      });
      if (result.__typename === 'SubmitAPInvoiceFailure') {
        const msg = `${result.class}: ${JSON.stringify(result.details)}`;
        setError(msg);
        onError(msg);
        setSubmitting(false);
        return;
      }
      onSuccess(result.newState);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      onError(msg);
      setSubmitting(false);
    }
  };

  return (
    <SidePanel
      open
      title={`Submit invoice — ${po.vendor?.legalName ?? po.poRef}`}
      width={640}
      onClose={onClose}
      onSave={() => formRef.current?.requestSubmit()}
      dirty={!submitting}
      saveLabel={submitting ? 'Submitting…' : 'Submit invoice'}
    >
      <form ref={formRef} onSubmit={onSubmit}>
          <div className="form-grid">
            <label className="form-row">
              <span className="form-label">Vendor invoice #</span>
              <input
                type="text"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                required
                placeholder="INV-12345"
              />
            </label>
            <label className="form-row">
              <span className="form-label">Invoice date</span>
              <input
                type="text"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                required
                placeholder="YYYY-MM-DD"
              />
            </label>
            <label className="form-row">
              <span className="form-label">Intake channel</span>
              <select
                value={intakeChannel}
                onChange={(e) => setIntakeChannel(e.target.value as APInvoiceIntakeChannel)}
                required
              >
                {INTAKE_CHANNELS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-row">
              <span className="form-label">Currency</span>
              <input type="text" value={po.currencyCode} readOnly className="mono" />
            </label>
            <label className="form-row form-row-full">
              <span className="form-label">Notes (optional)</span>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Internal notes about this invoice"
              />
            </label>
          </div>

          <div className="po-lines">
            <div className="po-lines-header">
              <span className="form-label">
                Lines (defaults to received qty × PO price — change to trigger variances)
              </span>
            </div>
            {eligibleLines.length === 0 ? (
              <div className="alert alert-neg" role="alert">
                No lines have receipts yet. Record a receipt first.
              </div>
            ) : (
              <table className="po-lines-table">
                <thead>
                  <tr>
                    <th>Bill</th>
                    <th>PO line</th>
                    <th>Description</th>
                    <th>Received</th>
                    <th>Bill qty</th>
                    <th>PO price</th>
                    <th>Bill price</th>
                    <th>Line total</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => {
                    const q = Number(l.quantity);
                    const p = Number(l.unitPrice);
                    const total =
                      Number.isFinite(q) && Number.isFinite(p) ? (q * p).toFixed(4) : '—';
                    return (
                      <tr key={l.poLineRef}>
                        <td>
                          <input
                            type="checkbox"
                            checked={l.enabled}
                            onChange={(e) =>
                              updateLine(l.poLineRef, { enabled: e.target.checked })
                            }
                            aria-label="Include this line"
                          />
                        </td>
                        <td className="mono">{l.poLineRef.slice(0, 12)}…</td>
                        <td>
                          <input
                            type="text"
                            value={l.description}
                            onChange={(e) =>
                              updateLine(l.poLineRef, { description: e.target.value })
                            }
                            disabled={!l.enabled}
                          />
                        </td>
                        <td className="mono">{l.defaultQty}</td>
                        <td>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={l.quantity}
                            onChange={(e) =>
                              updateLine(l.poLineRef, { quantity: e.target.value })
                            }
                            disabled={!l.enabled}
                            className="po-line-num"
                          />
                        </td>
                        <td className="mono">{l.defaultPrice}</td>
                        <td>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={l.unitPrice}
                            onChange={(e) =>
                              updateLine(l.poLineRef, { unitPrice: e.target.value })
                            }
                            disabled={!l.enabled}
                            className="po-line-num"
                          />
                        </td>
                        <td className="mono">{total}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={7} className="po-lines-foot-label">
                      Total
                    </td>
                    <td className="mono">{computedTotal}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          {error && (
            <div className="alert alert-neg" role="alert">
              {error}
            </div>
          )}

      </form>
    </SidePanel>
  );
}
